// --- Our Modules --------------------------------------------------------------------------------
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    value: string;
    onChange: (value: string) => void;
    options: { value: string; label: string }[];
    label?: string;
    disabled?: boolean;
};

/**
 * A field with two or three answers, shown as the answers themselves.
 *
 * A select hides two options behind a click and a menu, and then hides which one is chosen behind
 * the closed box. Sale or rent, residential or commercial, planning or building or ready — these are
 * short, fixed and few, so they are laid out and the chosen one is filled in, exactly as a unit's
 * features are. One glance reads the answer instead of one click.
 *
 * Pressing the chosen option again unanswers the field, which a group of radios cannot otherwise do:
 * "none of these yet" is a real state in a product where records are created as shells and filled in
 * later. It took a dismiss button to say that, and the button was a third thing in a field whose
 * whole point is that it holds two.
 */
export default function ChoiceInput({ value, onChange, options, label, disabled }: Props) {
    return (
        <div className="choice-input">
            {label && <span className="choice-input__label">{label}</span>}

            <div className="choice-input__options" role="group" aria-label={label}>
                {options.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        className={`choice-input__option${value === option.value ? " choice-input__option--on" : ""}`}
                        onClick={() => onChange(value === option.value ? "" : option.value)}
                        disabled={disabled}
                        aria-pressed={value === option.value}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
        </div>
    );
}
